import { ConditionState, ConditionType } from "@builder/hooks/useConditions";
import { CONDITION_ACTION, CONDITION_OPERATOR } from "./constants";
import { v4 } from "uuid";
import { ActionDetails, ActionType } from "@common/build/types";

export const convertConditionsToString = (data: ConditionState, flip: boolean = false): string => {
    const conditionStrings = data.conditions.map(innerArray => {
        const innerStrings = innerArray.map(condition => {
            return `[${condition.source}] ${condition.condition} [${condition.target}]`;
        });

        return `(${innerStrings.join(flip ? ` ${CONDITION_OPERATOR.OR} ` : ` ${CONDITION_OPERATOR.AND} `)})`;
    });
    return conditionStrings.join(flip ? ` ${CONDITION_OPERATOR.AND} ` : ` ${CONDITION_OPERATOR.OR} `);
};

export const parseConditionString = (conditionString: string, flip: boolean = false): ConditionState | undefined => {
    if (!conditionString) return undefined;

    const conditionGroups = conditionString
        .split(`) ${flip ? CONDITION_OPERATOR.AND : CONDITION_OPERATOR.OR} (`)
        .map(group => group.replace(/[()]/g, ""));
    const conditions: ConditionType[][] = [];

    // This has meaning in the condition parsing lint is throwing this error for nothing.
    // eslint-disable-next-line no-useless-escape
    const conditionRegex = /\[([^\]]+)\] ([^\[]+) \[([^\]]+)\]/;

    for (const group of conditionGroups) {
        const conditionStrings = group.split(` ${flip ? CONDITION_OPERATOR.OR : CONDITION_OPERATOR.AND} `);
        const conditionGroup: ConditionType[] = [];

        for (const condition of conditionStrings) {
            const match = condition.match(conditionRegex);
            if (match) {
                const [, source, conditionType, target] = match;

                conditionGroup.push({
                    _id: v4(),
                    source: source.trim(),
                    condition: conditionType.trim(),
                    target: target.trim()
                });
            }
        }

        conditions.push(conditionGroup);
    }

    return { conditions };
};

export function getActionType(conditionAction: CONDITION_ACTION): ActionDetails {
    switch (conditionAction) {
        case CONDITION_ACTION.HIDE:
            return { type: ActionType.HIDE };
        case CONDITION_ACTION.SHOW:
            return { type: ActionType.SHOW };
        case CONDITION_ACTION.AUTHENTICATE:
            return { type: ActionType.AUTHENTICATE, data: "" };
        case CONDITION_ACTION.DISABLE:
            return { type: ActionType.DISABLE };
        default:
            return { type: ActionType.HIDE };
    }
}

export function getConditionActionValue(actionType: ActionType | null, isAutomation: boolean = false): CONDITION_ACTION {
    switch (actionType) {
        case ActionType.HIDE:
            return CONDITION_ACTION.HIDE;
        case ActionType.SHOW:
            return CONDITION_ACTION.SHOW;
        case ActionType.AUTHENTICATE:
            return CONDITION_ACTION.AUTHENTICATE;
        case ActionType.DISABLE:
            return CONDITION_ACTION.DISABLE;
        default:
            return isAutomation ? CONDITION_ACTION.AUTHENTICATE : CONDITION_ACTION.HIDE;
    }
}

export function isEmpty(obj: Record<string, unknown>): boolean {
    return Object.keys(obj).length === 0;
}

export function validateConditions(state: ConditionState) {
    const newErrorState: Record<string, Record<string, boolean>> = {};

    for (const conditionArray of state.conditions) {
        for (const condition of conditionArray) {
            if (!condition.source || !condition.condition || !condition.target) {
                if (!newErrorState[condition._id]) {
                    newErrorState[condition._id] = {};
                }
                if (!condition.source) {
                    newErrorState[condition._id].source = true;
                }
                if (!condition.condition) {
                    newErrorState[condition._id].condition = true;
                }
                if (!condition.target) {
                    newErrorState[condition._id].target = true;
                }
            }
        }
    }

    return newErrorState;
}

export function extractSources(expression: string): string[] {
    const sourcePattern =
        /\[([^\]]+)\](?= is| more than| less than| contains any of| does not contain any of| contains all of| does not contain all of)/g;
    const sources = new Set<string>();
    let match;

    while ((match = sourcePattern.exec(expression)) !== null) {
        sources.add(match[1]);
    }

    return Array.from(sources);
}
